import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { useIntl } from 'umi';
import { useMount } from 'ahooks';
import Loading from '@intelligence/components/Loading';
import DateSelect from '@intelligence/v3/pages/CasualDailyReports/components/DateSelect';
import { fetchGlobalDynamicsDailyDates, fetchGlobalDynamicsDailyData } from '@intelligence/v3/services/globalDynamicsDaily';
import { getSortedDates } from '@intelligence/v3/utils/date';
import useSearchParams from '@intelligence/hooks/useSearchParams';
import { useQueryDefinition } from '@intelligence/hooks/useQueryDefinition';
import usePageLog from '@intelligence/v3/_hooks/usePageLog';
import { updateURLSearchParams } from '@intelligence/utils/commonUtils';
import { WorldWideTab } from './constants';
import MobileBreakouts from './components/MobileBreakouts';
import SteamBreakouts from './components/SteamBreakouts';
import RobloxBreakouts from './components/RobloxBreakouts';
import './style.scss';

// 导入图片
import LogoLi from '@intelligence/assets/imgs/CasualWordlWide/logo-li.png';
import LogoDatabrain from '@intelligence/assets/imgs/CasualWordlWide/logo-databrain-big.png';
import LogoGame from '@intelligence/assets/imgs/CasualWordlWide/logo-game.png';

const CasualWorldWide: React.FC = () => {
  const { formatMessage, locale } = useIntl();
  const { date: urlDate, tab: urlTab } = useSearchParams();
  const { genreOptionsMap } = useQueryDefinition();
  const [activeTab, setActiveTab] = useState<WorldWideTab>(
    (Object.values(WorldWideTab) as string[]).includes(urlTab) ? urlTab as WorldWideTab : WorldWideTab.Mobile,
  );
  const [loading, setLoading] = useState(true);
  const [reportDates, setReportDates] = useState<string[]>([]);
  const [reportDate, setReportDate] = useState('');
  const [reportData, setReportData] = useState<any>(null);

  // 切换 tab 时同步更新 URL 参数
  useEffect(() => {
    updateURLSearchParams({ tab: activeTab });
  }, [activeTab]);

  // 获取可选日期列表
  const updateReportDates = async (): Promise<string[]> => {
    const res = await fetchGlobalDynamicsDailyDates({
      start_date: moment().subtract(2, 'years').startOf('year').format('YYYY-MM-DD'),
      end_date: moment().endOf('year').format('YYYY-MM-DD'),
    });
    const dates = getSortedDates(res?.data || []);
    setReportDates(dates);
    return dates;
  };

  // 获取日报数据
  const updateReportDetails = async (date: string) => {
    setLoading(true);
    setReportDate(date);
    updateURLSearchParams({ date });
    try {
      const res = await fetchGlobalDynamicsDailyData({ report_date: date });
      setReportData(res?.data?.data || null);
    } catch (error) {
      console.error(error);
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  // 日期变更回调
  const handleReportDateChange = (date: string) => {
    void updateReportDetails(date);
  };

  // 页面打点
  usePageLog('globalGamingDynamics');

  // 初始化
  useMount(() => {
    setLoading(true);
    updateReportDates()
      .then((dates) => {
        if (dates.length > 0) {
          // 优先使用 URL 上的日期，否则使用最新日期
          const initialDate = urlDate && dates.includes(urlDate) ? urlDate : dates[dates.length - 1];
          void updateReportDetails(initialDate);
        }
      })
      .catch(() => {
        setLoading(false);
      });
  });

  return (
    <div className="dbi-casual-worldwide-reports">
      {loading && (
        <div className="dbi-casual-worldwide-reports-loading">
          <Loading content={formatMessage({ id: 'common_loading' })} />
        </div>
      )}
      <div className={`dbi-worldwide-page ${activeTab === WorldWideTab.Mobile ? ' dbi-worldwide-page-bg' : ''}`} style={{ display: loading ? 'none' : 'block' }}>
        {/* 头部 */}
        <div className="dbi-worldwide-header">
          {/* Logos */}
          <div className="dbi-worldwide-header-log-wrap">
            <img className="dbi-worldwide-header-logo" src={LogoLi} alt="logo" />
            <img className="dbi-worldwide-header-logo" src={LogoDatabrain} alt="logo" />
            <img className="dbi-worldwide-header-logo dbi-worldwide-header-logo--game" src={LogoGame} alt="logo" />
          </div>
          {/* 主标题 */}
          <h1 className="dbi-worldwide-main-title"> {/*** LoGo */}</h1>
          <div className="dbi-worldwide-update-date">{formatMessage({ id: 'dbi_worldwide_update_time' })}</div>
          {/* 标签栏 */}
          <div className="dbi-worldwide-tabs">
            <div className={`dbi-worldwide-tab ${activeTab === WorldWideTab.Mobile ? 'active' : ''}`} onClick={() => setActiveTab(WorldWideTab.Mobile)}>
              {formatMessage({ id: 'dbi_mobile' })}
            </div>
            <div className={`dbi-worldwide-tab ${activeTab === WorldWideTab.Steam ? 'active' : ''}`} onClick={() => setActiveTab(WorldWideTab.Steam)}>
              {formatMessage({ id: 'dbi_worldwide_tab_steam' })}
            </div>
            <div className={`dbi-worldwide-tab ${activeTab === WorldWideTab.Roblox ? 'active' : ''}`} onClick={() => setActiveTab(WorldWideTab.Roblox)}>
              {formatMessage({ id: 'dbi_worldwide_tab_roblox' })}
            </div>
          </div>
          {/* 日期选择 */}
          <div className="dbi-worldwide-reports-date-select">
            <DateSelect
              availableDates={reportDates}
              value={reportDate}
              onChange={handleReportDateChange}
            />
          </div>
        </div>

        {/* 内容区 */}
        <div className="dbi-worldwide-header-content">
          {/* 手游异动 Tab */}
          {activeTab === WorldWideTab.Mobile && (
            <MobileBreakouts data={reportData?.mobile || []} reportDate={reportData?.report_date} locale={locale} genreOptionsMap={genreOptionsMap} />
          )}

          {/* Steam异动 Tab */}
          {activeTab === WorldWideTab.Steam && (
            <SteamBreakouts data={reportData?.steam || null} reportDate={reportData?.report_date} locale={locale} genreOptionsMap={genreOptionsMap} />
          )}

          {/* Roblox异动 Tab */}
          {activeTab === WorldWideTab.Roblox && (
            <RobloxBreakouts data={reportData?.roblox || null} reportDate={reportData?.report_date} locale={locale} genreOptionsMap={genreOptionsMap} />
          )}
        </div>

        {/* 底部联系方式 */}
        <div className="dbi-worldwide-contact">
          <img src={LogoDatabrain} alt="" className="dbi-worldwide-footer-contact-logo" />
          <div className="dbi-worldwide-footer-contact-text">{formatMessage({ id: 'dbi_worldwide_contact_text' })}</div>
        </div>
      </div>
    </div>
  );
};

export default CasualWorldWide;
